'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { logoutRequest } from '@/services/auth';
import { removeToken, setToken } from '@/utils/auth';

type AuthContextType = {
  isAuthenticated: boolean;
  userId: number | null;
  login: (token: string) => void;
  logout: () => void;
  adminLogout: () => void;
};

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  userId: null,
  login: () => { },
  logout: () => { },
  adminLogout: () => { },
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    if (typeof window !== 'undefined') {
      return !!localStorage.getItem('access_token');
    }
    return false;
  });
  const [userId, setUserId] = useState<number | null>(null);
  const router = useRouter();

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const handler = () => {
      setIsAuthenticated(!!localStorage.getItem('access_token'));
    };
    
    if (token && isAuthenticated) {
      try {
        const payload = token.split('.')[1];
        const decodedPayload = JSON.parse(atob(payload ?? ''));
        setUserId(decodedPayload?.sub)
      } catch (e) {
        console.error('Не удалось декодировать токен', e);
      }
    }

    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const login = (token: string) => {
    setToken(token);
    setIsAuthenticated(true);
  };

  const clearAuthData = () => {
    removeToken();
    setIsAuthenticated(false);
  };

  const logout = (withRedirect = true) => {
    logoutRequest();
    clearAuthData();
    if (withRedirect) {
      router.push('/auth');
    }
  };

  const adminLogout = () => {
    logoutRequest();
    clearAuthData();
    router.push('/admin/auth/login');
  };

  // ! Global доступ к logout функциям
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__logout = logout;
      (window as any).__logoutWithoutRedirect = () => logout(false);
      (window as any).__adminLogout = adminLogout;
    }
    return () => {
      if (typeof window !== 'undefined') {
        (window as any).__logout = undefined;
        (window as any).__logoutWithoutRedirect = undefined;
        (window as any).__adminLogout = undefined;
      }
    };
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, adminLogout, userId }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}